import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, Loader2, AlertCircle, Eye, EyeOff, ArrowLeft, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../lib/toast';
import { InactiveInventoryDialog } from '../components/InactiveInventoryDialog';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Facebook OAuth configuration
const FACEBOOK_APP_ID = '759058827246526';
const FACEBOOK_REDIRECT_URI = 'https://wantd.au/auth/facebook/callback';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showInactiveDialog, setShowInactiveDialog] = useState(false);
  const [inactiveInventoryInfo, setInactiveInventoryInfo] = useState(null);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const { login, loginWithToken, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  // Handle Google Sign-In via Firebase
  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      const result = await loginWithGoogle();
      
      // Different message for new users vs existing users
      if (result.isNewUser) {
        toast.success('Welcome to WANTD! Account created with Google.');
      } else {
        toast.success('Welcome back! Signed in with Google.');
      }
      
      // Check for inactive inventory
      if (result.inactiveInventory?.has_inactive) {
        setInactiveInventoryInfo(result.inactiveInventory);
        setShowInactiveDialog(true);
        return;
      }
      
      // Redirect based on role
      if (result.isAdmin) {
        navigate('/admin', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error('Google login error:', error);
      if (error.message !== 'Sign-in cancelled') {
        toast.error(error.message || 'Failed to sign in with Google');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  // Handle Facebook OAuth - initiate the flow
  const handleFacebookLogin = () => {
    setFacebookLoading(true);
    
    // Redirect to Facebook OAuth
    const facebookAuthUrl = 
      `https://www.facebook.com/v18.0/dialog/oauth` +
      `?client_id=${FACEBOOK_APP_ID}` +
      `&redirect_uri=${encodeURIComponent(FACEBOOK_REDIRECT_URI)}` +
      `&scope=email,public_profile` +
      `&response_type=code`;
    
    window.location.href = facebookAuthUrl;
  };

  const handleResendVerification = async () => {
    if (!email || !password) {
      toast.error('Please enter your email and password first');
      return;
    }
    
    setResendingEmail(true);
    try {
      // Use our custom Resend-based verification email
      await axios.post(`${API_URL}/api/auth/custom/send-verification`, {
        email: email.toLowerCase()
      });
      
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      toast.error('Failed to send verification email. Please try again.');
    } finally {
      setResendingEmail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowVerificationPrompt(false);

    try {
      const { user, inactiveInventory, isAdmin } = await login(email, password);
      
      // Check if user has inactive inventory that needs reactivation
      if (inactiveInventory?.has_inactive && inactiveInventory?.item_count > 0) {
        setInactiveInventoryInfo(inactiveInventory);
        setShowInactiveDialog(true);
        // Don't navigate yet - wait for user to handle the dialog
        return;
      }
      
      // Redirect based on account type (admins go to admin portal)
      navigateAfterLogin(user, isAdmin);
    } catch (error) {
      // Handle specific Firebase error codes
      const errorCode = error.code;
      let errorMessage = 'Invalid credentials';
      
      if (errorCode === 'auth/email-not-verified') {
        errorMessage = error.message || 'Please verify your email before signing in.';
        setShowVerificationPrompt(true);
      } else if (errorCode === 'auth/user-not-found' || errorCode === 'auth/wrong-password' || errorCode === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password';
      } else if (errorCode === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const navigateAfterLogin = (user, isAdmin = false) => {
    // Admins always go to admin portal
    if (isAdmin) {
      navigate('/admin', { replace: true });
      return;
    }
    
    // Regular users - check for original intended destination, otherwise go home
    if (from !== '/') {
      navigate(from, { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  };

  const handleDialogClose = () => {
    setShowInactiveDialog(false);
    // After closing dialog, navigate to dashboard
    navigateAfterLogin({ account_type: 'both' }); // Default to dashboard
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative" style={{ backgroundColor: 'var(--theme-bg)' }}>
      <div className="w-full max-w-md">
        {/* Back/Close Button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 md:top-8 md:left-8 p-2 rounded-full hover:bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] transition-colors"
          data-testid="login-back-btn"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 right-4 md:top-8 md:right-8 p-2 rounded-full hover:bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] transition-colors"
          data-testid="login-close-btn"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Logo */}
        <Link to="/" className="flex items-center justify-center mb-8">
          <img 
            src="/logo-wantd-navy.png"
            alt="WANTD" 
            className="h-12 w-auto"
          />
        </Link>

        {/* Card */}
        <div className="bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-2xl p-8 shadow-card">
          <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] text-center mb-2">Welcome Back</h1>
          <p className="text-[var(--theme-text-secondary)] text-center mb-8">Sign in to continue to WANTD</p>

          {/* Email Verification Required Banner */}
          {showVerificationPrompt && (
            <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    Email verification required
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    Please check your inbox and click the verification link before signing in.
                  </p>
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendingEmail}
                    className="text-sm font-medium text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 mt-2 underline"
                  >
                    {resendingEmail ? 'Sending...' : 'Resend verification email'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label className="form-label">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--theme-text-muted)]" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10 bg-[var(--theme-bg)] border-[var(--theme-border)] text-[var(--theme-text-primary)] placeholder:text-[var(--theme-text-muted)]"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="form-label mb-0">Password</label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-[#C9956C] hover:text-[#B8845B]"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--theme-text-muted)]" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 bg-[var(--theme-bg)] border-[var(--theme-border)] text-[var(--theme-text-primary)] placeholder:text-[var(--theme-text-muted)]"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--theme-text-muted)] hover:text-[var(--theme-text-secondary)] transition-colors"
                  data-testid="login-password-toggle"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit */}
            <Button 
              type="submit" 
              className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white rounded-full h-12 dark:bg-[#C9956C] dark:text-[#1E3A5F] dark:hover:bg-[#B8845B]"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[var(--theme-border)]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-[var(--theme-card)] text-[var(--theme-text-muted)]">or continue with</span>
            </div>
          </div>

          {/* Social Login Options */}
          <div className="space-y-3">
            {/* Google Sign-In */}
            <Button 
              type="button"
              variant="outline"
              className="w-full border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:bg-[#4285F4]/10 hover:border-[#4285F4]/50 rounded-full h-12"
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              data-testid="google-login-btn"
            >
              {googleLoading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              Continue with Google
            </Button>

            {/* Facebook Sign-In - Coming Soon */}
            <Button 
              type="button"
              variant="outline"
              className="w-full border-[var(--theme-border)] text-[var(--theme-text-muted)] rounded-full h-12 cursor-not-allowed opacity-60"
              disabled={true}
              data-testid="facebook-login-btn"
            >
              <svg className="w-5 h-5 mr-2 opacity-50" fill="#9CA3AF" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Continue with Facebook
              <span className="ml-2 text-xs bg-[var(--theme-hover)] px-2 py-0.5 rounded-full">Coming Soon</span>
            </Button>
          </div>

          {/* Register Link */}
          <p className="text-center text-[var(--theme-text-secondary)] mt-6">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="text-[#C9956C] hover:text-[#B8845B] font-medium">
              Sign Up
            </Link>
          </p>
        </div>
      </div>

      {/* Inactive Inventory Dialog */}
      <InactiveInventoryDialog 
        isOpen={showInactiveDialog}
        onClose={handleDialogClose}
        daysInactive={inactiveInventoryInfo?.days_inactive || 0}
        itemCount={inactiveInventoryInfo?.item_count || 0}
      />
    </div>
  );
}
